import React, { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { formatDate } from "@/utils/timezone";
import { formatCurrency } from "@/utils/formatters";
import ColoredPercent from "./ColoredPercent";
import ColoredCurrency from "./ColoredCurrency";
import { useNotification } from "./NotificationContext";
import CreateAlertDialog from "./modals/CreateAlertDialog";
import IntelCard from "./IntelCard";
import IntelDetailModal from "./modals/IntelDetailModal";
import ImageViewerModal from "./ImageViewerModal";
import api from "@/api";

// Lazy-loaded components - only imported when needed
const TickerPriceChart = React.lazy(() => import("./TickerPriceChart"));
const CAGRTimelineChart = React.lazy(() => import("./CAGRTimelineChart"));
const TickerAccountsTable = React.lazy(() => import("./TickerAccountsTable"));

const TickerDetailsPage = ({ accounts }) => {
  const { symbol } = useParams();
  const [searchParams] = useSearchParams();
  const accountId = searchParams.get("accountId");
  const { showError } = useNotification();

  // Core state - always loaded
  const [loading, setLoading] = useState(true);
  const [securityData, setSecurityData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const pollRef = useRef(null);
  const [activeTab, setActiveTab] = useState("chart");
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [viewingImageIndex, setViewingImageIndex] = useState(null);
  const [viewingImageUrl, setViewingImageUrl] = useState(null);
  const [detailIntelId, setDetailIntelId] = useState(null);

  // Tab-specific state - lazy loaded
  const [transactions, setTransactions] = useState(null); // null = not loaded
  const [intel, setIntel] = useState(null); // null = not loaded
  const [tabLoading, setTabLoading] = useState({});

  const account = accounts?.find((a) => a.id === accountId);

  // Fetch security data on load; if prices are stale, kick a background refresh
  // and poll until they're fresh (mirrors the account sync-polling pattern).
  useEffect(() => {
    if (!symbol) return;
    setSecurityData(null);
    fetchSecurityData().then((data) => {
      if (data && (data.staleness?.is_stale || data.current_price == null)) {
        triggerRefreshAndPoll();
      }
    });

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      setRefreshing(false);
    };
  }, [symbol]);

  const fetchSecurityData = async ({ showSpinner = true } = {}) => {
    try {
      if (showSpinner) setLoading(true);
      const response = await api.get(`/securities/${symbol}`, {
        params: { check_staleness: true },
      });
      setSecurityData(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching security data:", error);
      if (showSpinner) showError(`Failed to load ticker data: ${error.message}`);
      return null;
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  // Trigger a background price refresh, then poll the details endpoint until prices
  // are fresh (capped). Updates the displayed price in place.
  const triggerRefreshAndPoll = async () => {
    setRefreshing(true);
    try {
      await api.post(`/securities/${symbol}/refresh`);
    } catch (error) {
      console.error("Price refresh trigger failed:", error);
    }

    let attempts = 0;
    const MAX_ATTEMPTS = 6; // ~18s
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      attempts += 1;
      const data = await fetchSecurityData({ showSpinner: false });
      const stillStale =
        !data || data.staleness?.is_stale || data.current_price == null;
      if (!stillStale || attempts >= MAX_ATTEMPTS) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setRefreshing(false);
      }
    }, 3000);
  };

  // Load tab data when tab changes - using refs to track loaded state
  useEffect(() => {
    const loadTransactions = async () => {
      if (transactions !== null || !accountId) return;

      try {
        setTabLoading((prev) => ({ ...prev, transactions: true }));
        const response = await api.get(`/accounts/${accountId}/transactions`, {
          params: {
            ticker: symbol,
            action: "ALL",
            page: 1,
            limit: 1000,
          },
        });
        setTransactions(response.data.transactions || []);
      } catch (error) {
        console.error("Error fetching transactions:", error);
        setTransactions([]);
      } finally {
        setTabLoading((prev) => ({ ...prev, transactions: false }));
      }
    };

    const loadIntel = async () => {
      if (intel !== null) return;

      try {
        setTabLoading((prev) => ({ ...prev, intel: true }));
        const response = await api.get(`/intel`, {
          params: { ticker: symbol },
        });
        setIntel(response.data.items || response.data || []);
      } catch (error) {
        console.error("Error fetching intel:", error);
        setIntel([]);
      } finally {
        setTabLoading((prev) => ({ ...prev, intel: false }));
      }
    };

    if (activeTab === "transactions" || activeTab === "timeline") {
      loadTransactions();
    }
    if (activeTab === "intel" || activeTab === "timeline") {
      loadIntel();
    }
  }, [activeTab, symbol, accountId, transactions, intel]);

  const handleAlertCreated = (newAlert) => {
    window.dispatchEvent(new CustomEvent("alertCreated", { detail: newAlert }));
  };

  // Combine timeline items - only when both are loaded
  const getTimelineItems = () => {
    if (transactions === null && intel === null) return [];

    const items = [];

    if (transactions) {
      transactions.forEach((txn) => {
        items.push({
          type: "transaction",
          date: new Date(txn.date),
          data: txn,
        });
      });
    }

    if (intel) {
      intel.forEach((item) => {
        items.push({
          type: "intel",
          date: new Date(item.timestamp),
          data: item,
        });
      });
    }

    return items.sort((a, b) => b.date - a.date);
  };

  const renderTransactionRow = (txn) => {
    const isBuy = txn.type === "BUY" || txn.type === "SHARES_IN";
    const isSell = txn.type === "SELL";
    const isDividend = txn.type === "DIVIDEND";

    const getBorderColor = () => {
      if (isBuy) return "var(--color-positive)";
      if (isSell) return "var(--color-negative)";
      return "var(--card-hover-border)";
    };

    return (
      <div
        key={`txn-${txn.id}`}
        style={{
          padding: "1rem",
          borderLeft: `4px solid ${getBorderColor()}`,
          backgroundColor: "var(--card-bg)",
          borderRadius: "0.5rem",
          marginBottom: "0.75rem",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                marginBottom: "0.5rem",
              }}
            >
              <span
                style={{
                  fontSize: "1.5rem",
                }}
              >
                {isBuy ? "📈" : isSell ? "📉" : "💰"}
              </span>
              <div>
                <div
                  style={{
                    fontWeight: "600",
                    fontSize: "1rem",
                    color: "var(--app-text)",
                  }}
                >
                  {txn.type}
                  {txn.shares && ` • ${txn.shares} shares`}
                  {txn.price && ` @ ${formatCurrency(txn.price)}`}
                </div>
                <div
                  style={{
                    fontSize: "0.875rem",
                    color: "var(--subtitle-color)",
                  }}
                >
                  {formatDate(txn.date)}
                </div>
              </div>
            </div>

            {txn.description && (
              <div
                style={{
                  fontSize: "0.875rem",
                  color: "var(--app-text)",
                  marginLeft: "2.5rem",
                }}
              >
                {txn.description}
              </div>
            )}
          </div>

          <div style={{ textAlign: "right" }}>
            {txn.amount_reported && (
              <div
                className={
                  isBuy ? "negative" : isSell || isDividend ? "positive" : ""
                }
                style={{
                  fontWeight: "600",
                  fontSize: "1.125rem",
                }}
              >
                {isBuy ? "-" : "+"}
                {formatCurrency(Math.abs(txn.amount_reported))}
              </div>
            )}
            {txn.fees > 0 && (
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "var(--subtitle-color)",
                }}
              >
                Fees: {formatCurrency(txn.fees)}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Get intel items with screenshots for image carousel
  const intelWithScreenshots = (intel || []).filter(
    (item) => item.screenshot_data,
  );
  const screenshotUrls = intelWithScreenshots.map((item) => item.screenshot_data);

  // Loading spinner component
  const TabSpinner = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "3rem",
      }}
    >
      <div className="spinner"></div>
      <span style={{ marginLeft: "1rem", color: "var(--text-muted)" }}>
        Loading...
      </span>
    </div>
  );

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        Loading ticker data...
      </div>
    );
  }

  const totalTransactions = securityData?.transactions_count || 0;
  const totalIntel = securityData?.intel_count || 0;

  // Derived data from lazy-loaded content
  const loadedTransactions = transactions || [];
  const loadedIntel = intel || [];
  const buyTransactions = loadedTransactions.filter(
    (t) => t.type === "BUY" || t.type === "SHARES_IN",
  );
  const sellTransactions = loadedTransactions.filter((t) => t.type === "SELL");
  const dividendTransactions = loadedTransactions.filter(
    (t) => t.type === "DIVIDEND",
  );

  const currentAccountHolding = accountId
    ? securityData?.holdings_by_account?.find((h) => h.account_id === accountId)
    : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Header — pinned, does not scroll */}
      <div className="dashboard-header" style={{ flexShrink: 0, background: "var(--app-bg)", padding: "1rem 2rem", marginBottom: 0 }}>
        <div>
          <h1 className="dashboard-title">{symbol}</h1>
          <p className="dashboard-subtitle">
            {securityData?.security?.name || "Loading..."}
            {securityData && (
              <>
                {" • "}
                {formatCurrency(securityData.current_price || 0)}
                {securityData.price_change && (
                  <>
                    {" "}
                    <ColoredCurrency value={securityData.price_change} />
                    {" ("}
                    <ColoredPercent value={securityData.price_change_percent} />
                    {")"}
                  </>
                )}
                {refreshing && (
                  <span
                    style={{
                      marginLeft: "0.75rem",
                      fontSize: "0.8125rem",
                      color: "var(--text-muted)",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.35rem",
                    }}
                  >
                    <span className="spinner" style={{ width: "0.75rem", height: "0.75rem" }} />
                    updating…
                  </span>
                )}
              </>
            )}
          </p>
        </div>

        {/* Set Alert Button */}
        {securityData && (
          <button
            onClick={() => setIsAlertModalOpen(true)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
              />
            </svg>
            Set Alert
          </button>
        )}
      </div>

      {/* Scrollable body */}
      <div style={{ flex: 1, overflowY: "auto" }}>
      <div style={{ padding: "2rem", maxWidth: "1400px", margin: "0 auto" }}>

      {/* All Accounts Table - Lazy loaded */}
      {securityData && (
        <React.Suspense fallback={<TabSpinner />}>
          <TickerAccountsTable symbol={symbol} securityData={securityData} />
        </React.Suspense>
      )}

      {/* Ticker Summary Cards - Show account-specific if viewing from account page */}
      {accountId && currentAccountHolding && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "1rem",
            marginBottom: "2rem",
          }}
        >
          <div className="account-card">
            <div className="stat-label">
              Your Position in {account?.account?.name}
            </div>
            <div className="stat-value" style={{ fontSize: "1.5rem" }}>
              {currentAccountHolding.shares} shares
            </div>
            <div
              className="text-muted"
              style={{ fontSize: "0.75rem", marginTop: "0.25rem" }}
            >
              Entry: {formatCurrency(currentAccountHolding.avg_cost_basis || 0)}
            </div>
          </div>

          <div className="account-card">
            <div className="stat-label">Market Value</div>
            <div className="stat-value" style={{ fontSize: "1.5rem" }}>
              {formatCurrency(currentAccountHolding.market_value || 0)}
            </div>
            <div style={{ fontSize: "0.875rem", marginTop: "0.25rem" }}>
              <span className="text-muted">Cost: </span>
              {formatCurrency(currentAccountHolding.cost_basis_total || 0)}
            </div>
          </div>

          <div className="account-card">
            <div className="stat-label">Unrealized P&L</div>
            <div className="stat-value" style={{ fontSize: "1.5rem" }}>
              <ColoredCurrency
                value={currentAccountHolding.unrealized_gain_loss || 0}
              />
            </div>
            <div style={{ fontSize: "0.875rem", marginTop: "0.25rem" }}>
              <ColoredPercent
                value={currentAccountHolding.unrealized_gain_loss_percent || 0}
              />
            </div>
          </div>

          <div className="account-card">
            <div className="stat-label">Activity</div>
            <div className="stat-value" style={{ fontSize: "1.5rem" }}>
              {totalTransactions + totalIntel}
            </div>
            <div
              className="text-muted"
              style={{ fontSize: "0.75rem", marginTop: "0.25rem" }}
            >
              {totalTransactions} transactions • {totalIntel} intel
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{ marginBottom: "2rem" }}>
        <div
          className="border-subtle"
          style={{ display: "flex", borderBottom: "1px solid" }}
        >
          <button
            className={`tab-button ${activeTab === "chart" ? "active" : ""}`}
            onClick={() => setActiveTab("chart")}
            style={{
              padding: "1rem 2rem",
              fontWeight: activeTab === "chart" ? "600" : "500",
            }}
          >
            📈 Chart
          </button>
          <button
            className={`tab-button ${
              activeTab === "performance" ? "active" : ""
            }`}
            onClick={() => setActiveTab("performance")}
            style={{
              padding: "1rem 2rem",
              fontWeight: activeTab === "performance" ? "600" : "500",
            }}
          >
            📊 Performance
          </button>
          <button
            className={`tab-button ${activeTab === "timeline" ? "active" : ""}`}
            onClick={() => setActiveTab("timeline")}
            style={{
              padding: "1rem 2rem",
              fontWeight: activeTab === "timeline" ? "600" : "500",
            }}
          >
            🕐 Full History
          </button>
          <button
            className={`tab-button ${
              activeTab === "transactions" ? "active" : ""
            }`}
            onClick={() => setActiveTab("transactions")}
            style={{
              padding: "1rem 2rem",
              fontWeight: activeTab === "transactions" ? "600" : "500",
            }}
          >
            💱 Transactions ({totalTransactions})
          </button>
          <button
            className={`tab-button ${activeTab === "intel" ? "active" : ""}`}
            onClick={() => setActiveTab("intel")}
            style={{
              padding: "1rem 2rem",
              fontWeight: activeTab === "intel" ? "600" : "500",
            }}
          >
            📸 Intel ({totalIntel})
          </button>
        </div>

        {/* Tab Content - Only render active tab */}
        <div style={{ marginTop: "1.5rem", minHeight: "400px" }}>
          {activeTab === "chart" && (
            <React.Suspense fallback={<TabSpinner />}>
              <TickerPriceChart ticker={symbol} />
            </React.Suspense>
          )}

          {activeTab === "performance" && (
            <div>
              <div className="section-content">
                <h3 className="section-title">Performance Trajectory</h3>
                <p
                  className="section-description"
                  style={{ marginBottom: "1.5rem" }}
                >
                  CAGR (Compound Annual Growth Rate) calculated from different
                  time periods to show momentum trends
                </p>
              </div>
              {accountId ? (
                <React.Suspense fallback={<TabSpinner />}>
                  <CAGRTimelineChart ticker={symbol} accountId={accountId} />
                </React.Suspense>
              ) : (
                <div
                  style={{
                    padding: "2rem",
                    textAlign: "center",
                    color: "var(--muted-foreground)",
                  }}
                >
                  <p>
                    Select an account view to see performance trajectory for
                    this ticker
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === "timeline" && (
            <div>
              <div className="section-content">
                <h3 className="section-title">Complete Timeline</h3>
                <p
                  className="section-description"
                  style={{ marginBottom: "1.5rem" }}
                >
                  All transactions, intel, and activity for {symbol} in
                  chronological order
                </p>
              </div>

              {tabLoading.transactions || tabLoading.intel ? (
                <TabSpinner />
              ) : getTimelineItems().length === 0 ? (
                <div className="empty-state">
                  <h3>No activity yet</h3>
                  <p>No transactions or intel found for {symbol}</p>
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                  }}
                >
                  {getTimelineItems().map((item, index) =>
                    item.type === "transaction" ? (
                      renderTransactionRow(item.data)
                    ) : (
                      <IntelCard
                        key={item.data.id || `intel-${index}`}
                        intel={item.data}
                        onScreenshotClick={(intel) => {
                          const imageIndex = intelWithScreenshots.findIndex(
                            (img) => img.id === intel.id,
                          );
                          setViewingImageIndex(imageIndex);
                        }}
                        onImageUrlClick={(url) => setViewingImageUrl(url)}
                        onDetailClick={(intel) => setDetailIntelId(intel.id)}
                        accountId={accountId}
                        showTickers={false}
                        showDeleteButton={false}
                      />
                    ),
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === "transactions" && (
            <div>
              <div className="section-content">
                <h3 className="section-title">Transactions</h3>
                {transactions !== null && (
                  <p
                    className="section-description"
                    style={{ marginBottom: "1.5rem" }}
                  >
                    {buyTransactions.length} buys • {sellTransactions.length}{" "}
                    sells • {dividendTransactions.length} dividends
                  </p>
                )}
              </div>

              {tabLoading.transactions ? (
                <TabSpinner />
              ) : !accountId ? (
                <div className="empty-state">
                  <h3>Select an account</h3>
                  <p>Choose an account above to view transactions</p>
                </div>
              ) : loadedTransactions.length === 0 ? (
                <div className="empty-state">
                  <h3>No transactions yet</h3>
                  <p>No transactions found for {symbol}</p>
                </div>
              ) : (
                <div>
                  {loadedTransactions.map((txn) => renderTransactionRow(txn))}
                </div>
              )}
            </div>
          )}

          {activeTab === "intel" && (
            <div>
              {tabLoading.intel ? (
                <TabSpinner />
              ) : loadedIntel.length === 0 ? (
                <div className="empty-state">
                  <h3>No intel yet</h3>
                  <p>No intel found for {symbol}</p>
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1.5rem",
                    maxWidth: "900px",
                  }}
                >
                  {loadedIntel.map((item, index) => (
                    <IntelCard
                      key={item.id || index}
                      intel={item}
                      onScreenshotClick={(intel) => {
                        const imageIndex = intelWithScreenshots.findIndex(
                          (img) => img.id === intel.id,
                        );
                        setViewingImageIndex(imageIndex);
                      }}
                      onImageUrlClick={(url) => setViewingImageUrl(url)}
                      onDetailClick={(intel) => setDetailIntelId(intel.id)}
                      accountId={accountId}
                      showTickers={false}
                      showDeleteButton={false}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      </div>{/* end inner padding wrapper */}
      </div>{/* end scrollable body */}

      {/* Create Alert Modal */}
      <CreateAlertDialog
        isOpen={isAlertModalOpen}
        onClose={() => setIsAlertModalOpen(false)}
        ticker={symbol}
        currentPrice={securityData?.current_price || 0}
        onAlertCreated={handleAlertCreated}
      />

      {/* Image Viewer Modal - for screenshot carousel */}
      <ImageViewerModal
        imageUrl={
          viewingImageIndex !== null ? screenshotUrls[viewingImageIndex] : null
        }
        onClose={() => setViewingImageIndex(null)}
        images={screenshotUrls}
        currentIndex={viewingImageIndex}
        onNavigate={setViewingImageIndex}
      />

      {/* Image Viewer Modal - for single image URLs (StockTwits charts) */}
      <ImageViewerModal
        imageUrl={viewingImageUrl}
        onClose={() => setViewingImageUrl(null)}
      />

      {detailIntelId && (
        <IntelDetailModal
          intelId={detailIntelId}
          onClose={() => setDetailIntelId(null)}
        />
      )}
    </div>
  );
};

export default TickerDetailsPage;
